import React, { useState } from 'react';
import { User, Mail, Globe, Bell, Shield, Key, Save, Moon, Sun, Monitor, Sparkles, Table2, Zap, Wind, TrendingUp, BarChart3, ArrowLeftRight, Activity, LineChart, Map, Euro, Settings2, Clock, Trash2, Plus, CheckCircle2, AlertTriangle, Smartphone, Laptop } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { useUserProfile } from '../hooks/useUserProfile';
import { useTheme } from '../hooks/useTheme';
const ATHENA_PRESETS = [{
  id: 'tables',
  label: 'Always use tables for comparisons',
  icon: Table2,
  category: 'Forecast Analysis'
}, {
  id: 'deltas',
  label: 'Show run deltas (Δ) in all forecasts',
  icon: TrendingUp,
  category: 'Forecast Analysis'
}, {
  id: 'divergence',
  label: 'Flag model divergence >15% as ⚠ signal',
  icon: AlertTriangle,
  category: 'Forecast Analysis'
}, {
  id: 'confidence',
  label: 'Tag confidence levels (HIGH/MED/LOW)',
  icon: Sparkles,
  category: 'Forecast Analysis'
}, {
  id: 'spreads',
  label: 'Show DA vs ID price spreads',
  icon: BarChart3,
  category: 'Market Data'
}, {
  id: 'wind',
  label: 'Include wind capacity factors',
  icon: Wind,
  category: 'Market Data'
}, {
  id: 'flows',
  label: 'Include cross-border flow analysis',
  icon: ArrowLeftRight,
  category: 'Cross-Border'
}, {
  id: 'balancing',
  label: 'Show balancing market signals',
  icon: Activity,
  category: 'Cross-Border'
}];
const JUA_MODELS = ['EPT-2', 'EPT-2 Early', 'EPT-2 RR', 'EPT-2 HRRR', 'EPT-2e'];
const EXTERNAL_MODELS = ['EC AIFS', 'EC IFS', 'GFS', 'ICON Global', 'ICON-EU', 'Microsoft Aurora', 'EC ENS'];
type TabId = 'profile' | 'data' | 'athena' | 'notifications' | 'api' | 'security';
export function AccountSettingsView() {
  const {
    profile,
    updateProfile,
    isDirty,
    saveProfile,
    resetProfile
  } = useUserProfile();
  const {
    theme,
    setTheme
  } = useTheme();
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const handleSave = () => {
    saveProfile();
    toast.success('Settings saved successfully', {
      description: 'Your preferences have been updated.'
    });
  };
  const toggleModel = (model: string) => {
    const current = profile.defaultModels;
    const next = current.includes(model) ? current.filter(m => m !== model) : [...current, model];
    updateProfile({
      defaultModels: next
    });
  };
  const togglePreset = (id: string) => {
    updateProfile({
      athenaPresets: {
        ...profile.athenaPresets,
        [id]: !profile.athenaPresets[id]
      }
    });
  };
  const toggleCalendar = (cal: string) => {
    const current = profile.tradingCalendars;
    const next = current.includes(cal) ? current.filter(c => c !== cal) : [...current, cal];
    updateProfile({
      tradingCalendars: next
    });
  };
  const handleGenerateKey = () => {
    const newKey = {
      id: `key-${Date.now()}`,
      name: 'New API Key',
      key: `jua_live_${Math.random().toString(36).substring(2, 10)}...`,
      permissions: 'Read-only',
      created: new Date().toISOString().split('T')[0],
      lastUsed: 'Never',
      callsToday: 0,
      rateLimit: 10000
    };
    updateProfile({
      apiKeys: [...profile.apiKeys, newKey]
    });
    toast.success('API Key generated', {
      description: "Make sure to copy your key now. You won't be able to see it again."
    });
  };
  const handleDeleteKey = (id: string) => {
    updateProfile({
      apiKeys: profile.apiKeys.filter(k => k.id !== id)
    });
    toast.success('API Key deleted');
  };
  const renderTabNav = () => {
    const tabs: {
      id: TabId;
      label: string;
      icon: React.ElementType;
    }[] = [{
      id: 'profile',
      label: 'Profile & Preferences',
      icon: User
    }, {
      id: 'data',
      label: 'Data & Display',
      icon: Settings2
    }, {
      id: 'athena',
      label: 'Athena AI Presets',
      icon: Sparkles
    }, {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell
    }, {
      id: 'api',
      label: 'API Keys',
      icon: Key
    }, {
      id: 'security',
      label: 'Security',
      icon: Shield
    }];
    return <div className="lg:col-span-1 space-y-2" data-id="element-2296">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${activeTab === tab.id ? 'bg-[#EC4899]/10 text-[#EC4899]' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`} data-id="element-2297">
            <tab.icon size={18} data-id="element-2298" />
            {tab.label}
          </button>)}
      </div>;
  };
  return <div className="jua-settings-page jua-page-bg p-8 max-w-5xl mx-auto space-y-8 pb-32" data-id="element-2299">
      <Toaster theme={theme === 'light' ? 'light' : 'dark'} data-id="element-2300" />

      <div data-id="element-2301">
        <h1 className="text-2xl font-bold text-white mb-2" data-id="element-2302">Account Settings</h1>
        <p className="text-slate-400" data-id="element-2303">
          Manage your profile, preferences, and platform configuration.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-2304">
        {renderTabNav()}

        <div className="lg:col-span-3 space-y-6" data-id="element-2305">
          {/* PROFILE TAB */}
          {activeTab === 'profile' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2306">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2307">
                <User size={20} className="text-[#EC4899]" data-id="element-2308" />
                Personal Information
              </h2>
              <div className="space-y-6" data-id="element-2309">
                <div className="flex items-center gap-6" data-id="element-2310">
                  <div className="h-20 w-20 rounded-full bg-[#EC4899] flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-pink-900/30" data-id="element-2311">
                    {profile.firstName[0]}
                    {profile.lastName[0]}
                  </div>
                  <div data-id="element-2312">
                    <Button variant="secondary" className="mb-2" data-id="element-2313">
                      Change Avatar
                    </Button>
                    <p className="text-xs text-slate-500" data-id="element-2314">
                      JPG, GIF or PNG. Max size of 800K
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2315">
                  <div className="space-y-2" data-id="element-2316">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2317">
                      First Name
                    </label>
                    <Input value={profile.firstName} onChange={e => updateProfile({
                  firstName: e.target.value
                })} data-id="element-2318" />
                  </div>
                  <div className="space-y-2" data-id="element-2319">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2320">
                      Last Name
                    </label>
                    <Input value={profile.lastName} onChange={e => updateProfile({
                  lastName: e.target.value
                })} data-id="element-2321" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-2322">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2323">
                    Email Address
                  </label>
                  <Input value={profile.email} onChange={e => updateProfile({
                email: e.target.value
              })} icon={<Mail size={16} data-id="element-2325" />} data-id="element-2324" />
                </div>

                <div className="pt-6 border-t border-slate-800 space-y-6" data-id="element-2326">
                  <h3 className="text-md font-semibold text-white" data-id="element-2327">
                    Localization & Appearance
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2328">
                    <div className="space-y-2" data-id="element-2329">
                      <label className="text-sm font-medium text-slate-300" data-id="element-2330">
                        Timezone
                      </label>
                      <div className="relative" data-id="element-2331">
                        <select value={profile.timezone} onChange={e => updateProfile({
                      timezone: e.target.value
                    })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2332">
                          <option value="UTC" data-id="element-2333">UTC</option>
                          <option value="Europe/London" data-id="element-2334">
                            London (GMT/BST)
                          </option>
                          <option value="Europe/Paris" data-id="element-2335">Paris (CET/CEST)</option>
                          <option value="Europe/Berlin" data-id="element-2336">
                            Berlin (CET/CEST)
                          </option>
                          <option value="Europe/Zurich" data-id="element-2337">
                            Zurich (CET/CEST)
                          </option>
                          <option value="Europe/Stockholm" data-id="element-2338">
                            Stockholm (CET/CEST)
                          </option>
                          <option value="America/New_York" data-id="element-2339">
                            New York (EST/EDT)
                          </option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2340">
                          <Globe size={16} data-id="element-2341" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-2342">
                      <label className="text-sm font-medium text-slate-300" data-id="element-2343">
                        Measurement Units
                      </label>
                      <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-full" data-id="element-2344">
                        <button onClick={() => updateProfile({
                      units: 'metric'
                    })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.units === 'metric' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2345">
                          Metric (°C, m/s)
                        </button>
                        <button onClick={() => updateProfile({
                      units: 'imperial'
                    })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.units === 'imperial' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2346">
                          Imperial (°F, mph)
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-2347">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2348">
                      Appearance
                    </label>
                    <div className="grid grid-cols-3 gap-3" data-id="element-2349">
                      <button onClick={() => setTheme('light')} className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${theme === 'light' ? 'border-[#EC4899] bg-[#EC4899]/10 text-[#EC4899]' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600'}`} data-id="element-2350">
                        <Sun size={20} data-id="element-2351" />
                        <span className="text-xs font-medium" data-id="element-2352">Light</span>
                      </button>
                      <button onClick={() => setTheme('dark')} className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all ${theme === 'dark' ? 'border-[#EC4899] bg-[#EC4899]/10 text-[#EC4899]' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600'}`} data-id="element-2353">
                        <Moon size={20} data-id="element-2354" />
                        <span className="text-xs font-medium" data-id="element-2355">Dark</span>
                      </button>
                      <button onClick={() => setTheme('dark')} // Mock system theme
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border transition-all border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600`} data-id="element-2356">
                        <Monitor size={20} data-id="element-2357" />
                        <span className="text-xs font-medium" data-id="element-2358">System</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>}

          {/* DATA & DISPLAY TAB */}
          {activeTab === 'data' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2359">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2360">
                <Settings2 size={20} className="text-[#EC4899]" data-id="element-2361" />
                Data & Display Defaults
              </h2>

              <div className="space-y-8" data-id="element-2362">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2363">
                  <div className="space-y-2" data-id="element-2364">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2365">
                      Default Chart Timeframe
                    </label>
                    <div className="relative" data-id="element-2366">
                      <select value={profile.defaultChartTimeframe} onChange={e => updateProfile({
                    defaultChartTimeframe: e.target.value as any
                  })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2367">
                        <option value="6h" data-id="element-2368">6 Hours</option>
                        <option value="12h" data-id="element-2369">12 Hours</option>
                        <option value="24h" data-id="element-2370">24 Hours</option>
                        <option value="48h" data-id="element-2371">48 Hours</option>
                        <option value="7d" data-id="element-2372">7 Days</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2373">
                        <LineChart size={16} data-id="element-2374" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-2375">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2376">
                      Default Map Layer
                    </label>
                    <div className="relative" data-id="element-2377">
                      <select value={profile.defaultMapLayer} onChange={e => updateProfile({
                    defaultMapLayer: e.target.value as any
                  })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2378">
                        <option value="wind" data-id="element-2379">Wind Speed</option>
                        <option value="solar" data-id="element-2380">Solar Irradiance</option>
                        <option value="temperature" data-id="element-2381">Temperature</option>
                        <option value="pressure" data-id="element-2382">Pressure</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2383">
                        <Map size={16} data-id="element-2384" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2385">
                  <div className="space-y-2" data-id="element-2386">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2387">
                      Price Currency
                    </label>
                    <div className="relative" data-id="element-2388">
                      <select value={profile.priceCurrency} onChange={e => updateProfile({
                    priceCurrency: e.target.value
                  })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2389">
                        <option value="EUR" data-id="element-2390">EUR (€)</option>
                        <option value="GBP" data-id="element-2391">GBP (£)</option>
                        <option value="NOK" data-id="element-2392">NOK (kr)</option>
                        <option value="SEK" data-id="element-2393">SEK (kr)</option>
                        <option value="DKK" data-id="element-2394">DKK (kr)</option>
                        <option value="PLN" data-id="element-2395">PLN (zł)</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2396">
                        <Euro size={16} data-id="element-2397" />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-2398">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2399">
                      Default Bidding Zone
                    </label>
                    <div className="relative" data-id="element-2400">
                      <select value={profile.defaultBiddingZone} onChange={e => updateProfile({
                    defaultBiddingZone: e.target.value
                  })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2401">
                        <option value="DE-LU" data-id="element-2402">
                          DE-LU (Germany/Luxembourg)
                        </option>
                        <option value="FR" data-id="element-2403">FR (France)</option>
                        <option value="GB" data-id="element-2404">GB (Great Britain)</option>
                        <option value="NL" data-id="element-2405">NL (Netherlands)</option>
                        <option value="NO2" data-id="element-2406">NO2 (Southern Norway)</option>
                        <option value="SE3" data-id="element-2407">SE3 (Stockholm)</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2408">
                        <MapPin size={16} data-id="element-2409" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2410">
                  <div className="space-y-2" data-id="element-2411">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2412">
                      Capacity Display
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-full" data-id="element-2413">
                      <button onClick={() => updateProfile({
                    capacityDisplay: 'absolute'
                  })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.capacityDisplay === 'absolute' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2414">
                        Absolute (GW)
                      </button>
                      <button onClick={() => updateProfile({
                    capacityDisplay: 'percentage'
                  })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.capacityDisplay === 'percentage' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2415">
                        Factor (%)
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-2416">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2417">
                      Price Display
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-full" data-id="element-2418">
                      <button onClick={() => updateProfile({
                    priceDisplay: 'mwh'
                  })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.priceDisplay === 'mwh' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2419">
                        / MWh
                      </button>
                      <button onClick={() => updateProfile({
                    priceDisplay: 'kwh'
                  })} className={`flex-1 py-2 text-sm font-medium transition-colors ${profile.priceDisplay === 'kwh' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2420">
                        / kWh
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2421">
                  <div className="space-y-2" data-id="element-2422">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2423">
                      Price Precision (Decimals)
                    </label>
                    <div className="flex items-center gap-4" data-id="element-2424">
                      <input type="range" min="0" max="4" value={profile.pricePrecision} onChange={e => updateProfile({
                    pricePrecision: parseInt(e.target.value)
                  })} className="flex-1 accent-pink-500" data-id="element-2425" />
                      <span className="text-white font-mono w-8 text-right" data-id="element-2426">
                        {profile.pricePrecision}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500" data-id="element-2427">
                      Example: €45.{'0'.repeat(profile.pricePrecision)}
                    </p>
                  </div>

                  <div className="space-y-2" data-id="element-2428">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2429">
                      Power Precision (Decimals)
                    </label>
                    <div className="flex items-center gap-4" data-id="element-2430">
                      <input type="range" min="0" max="3" value={profile.powerPrecision} onChange={e => updateProfile({
                    powerPrecision: parseInt(e.target.value)
                  })} className="flex-1 accent-pink-500" data-id="element-2431" />
                      <span className="text-white font-mono w-8 text-right" data-id="element-2432">
                        {profile.powerPrecision}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500" data-id="element-2433">
                      Example: 12.{'0'.repeat(profile.powerPrecision)} GW
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t border-slate-800" data-id="element-2434">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2435">
                    Trading Calendars
                  </label>
                  <p className="text-xs text-slate-500 mb-2" data-id="element-2436">
                    Select which market holidays and daylight saving rules to
                    observe.
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-2437">
                    {['EPEX', 'Nord Pool', 'N2EX', 'GME', 'OMIE', 'BSP'].map(cal => <label key={cal} className="flex items-center gap-2 cursor-pointer group" data-id="element-2438">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${profile.tradingCalendars.includes(cal) ? 'bg-[#EC4899] border-[#EC4899]' : 'bg-[#1E293B] border-slate-600 group-hover:border-slate-500'}`} data-id="element-2439">
                            {profile.tradingCalendars.includes(cal) && <CheckCircle2 size={14} className="text-white" data-id="element-2440" />}
                          </div>
                          <span className="text-sm text-slate-300 group-hover:text-white" data-id="element-2441">
                            {cal}
                          </span>
                          <input type="checkbox" className="hidden" checked={profile.tradingCalendars.includes(cal)} onChange={() => toggleCalendar(cal)} data-id="element-2442" />
                        </label>)}
                  </div>
                </div>
              </div>
            </Card>}

          {/* ATHENA AI TAB */}
          {activeTab === 'athena' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2443">
              <div className="flex items-center justify-between mb-6" data-id="element-2444">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2" data-id="element-2445">
                  <Sparkles size={20} className="text-[#EC4899]" data-id="element-2446" />
                  Athena AI Configuration
                </h2>
                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" data-id="element-2447">
                  Reset to Defaults
                </Button>
              </div>
              <p className="text-sm text-slate-400 mb-8" data-id="element-2448">
                Configure how Athena responds — optimized for energy trading
                speed and precision.
              </p>

              <div className="space-y-8" data-id="element-2449">
                <div className="space-y-2" data-id="element-2450">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2451">
                    Response Style
                  </label>
                  <div className="flex rounded-xl overflow-hidden border border-slate-700/60 w-fit" data-id="element-2452">
                    <button onClick={() => updateProfile({
                  responseStyle: 'concise'
                })} className={`px-6 py-2 text-sm font-medium transition-colors ${profile.responseStyle === 'concise' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2453">
                      ⚡ Concise
                    </button>
                    <button onClick={() => updateProfile({
                  responseStyle: 'tables-only'
                })} className={`px-6 py-2 text-sm font-medium transition-colors ${profile.responseStyle === 'tables-only' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2454">
                      📊 Tables Only
                    </button>
                    <button onClick={() => updateProfile({
                  responseStyle: 'detailed'
                })} className={`px-6 py-2 text-sm font-medium transition-colors ${profile.responseStyle === 'detailed' ? 'bg-[#EC4899] text-white' : 'bg-[#1E293B] text-slate-400 hover:text-white'}`} data-id="element-2455">
                      📝 Detailed
                    </button>
                  </div>
                </div>

                <div className="space-y-2" data-id="element-2456">
                  <label className="text-sm font-medium text-slate-300" data-id="element-2457">
                    Default Region Context
                  </label>
                  <div className="relative max-w-md" data-id="element-2458">
                    <select value={profile.defaultRegion} onChange={e => updateProfile({
                  defaultRegion: e.target.value
                })} className="w-full h-10 px-3 pr-10 rounded-xl bg-[#1E293B] border border-slate-700/60 text-sm text-white focus:outline-none focus:border-[#EC4899] focus:ring-1 focus:ring-[#EC4899] appearance-none" data-id="element-2459">
                      <option value="central-europe" data-id="element-2460">
                        Central Europe (DE, FR, NL, AT, CH)
                      </option>
                      <option value="nordics" data-id="element-2461">Nordics (NO, SE, FI, DK)</option>
                      <option value="iberia" data-id="element-2462">Iberia (ES, PT)</option>
                      <option value="uk-ireland" data-id="element-2463">UK & Ireland</option>
                      <option value="italy" data-id="element-2464">Italy</option>
                      <option value="eastern-europe" data-id="element-2465">
                        Eastern Europe (PL, CZ, HU, RO)
                      </option>
                      <option value="all-europe" data-id="element-2466">All Europe</option>
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400" data-id="element-2467">
                      <Globe size={16} data-id="element-2468" />
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-2469">
                  <div data-id="element-2470">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2471">
                      Default Models for Comparison
                    </label>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-2472">
                      Athena will prioritize these models in delta and
                      divergence tables.
                    </p>
                  </div>

                  <div className="space-y-3" data-id="element-2473">
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2474">
                      Jua Models
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-2475">
                      {JUA_MODELS.map(model => <button key={model} onClick={() => toggleModel(model)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${profile.defaultModels.includes(model) ? 'border-[#EC4899] bg-[#EC4899]/15 text-[#EC4899]' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-2476">
                          {model}
                        </button>)}
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-2477">
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2478">
                      External Models
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-2479">
                      {EXTERNAL_MODELS.map(model => <button key={model} onClick={() => toggleModel(model)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${profile.defaultModels.includes(model) ? 'border-blue-500 bg-blue-500/15 text-blue-400' : 'border-slate-700/60 bg-[#1E293B] text-slate-400 hover:border-slate-600 hover:text-slate-300'}`} data-id="element-2480">
                          {model}
                        </button>)}
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-800 space-y-6" data-id="element-2481">
                  <h3 className="text-md font-semibold text-white" data-id="element-2482">
                    Auto-Include Presets
                  </h3>

                  {['Forecast Analysis', 'Market Data', 'Cross-Border'].map(category => <div key={category} className="space-y-3" data-id="element-2483">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-2484">
                          {category}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-2485">
                          {ATHENA_PRESETS.filter(p => p.category === category).map(preset => <button key={preset.id} onClick={() => togglePreset(preset.id)} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${profile.athenaPresets[preset.id] ? 'bg-slate-800/80 border-slate-700' : 'bg-[#1E293B]/50 border-slate-800 hover:bg-[#1E293B]'}`} data-id="element-2486">
                              <div className="flex items-center gap-3" data-id="element-2487">
                                <preset.icon size={16} className={profile.athenaPresets[preset.id] ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2488" />
                                <span className={`text-sm font-medium ${profile.athenaPresets[preset.id] ? 'text-white' : 'text-slate-400'}`} data-id="element-2489">
                                  {preset.label}
                                </span>
                              </div>
                              <div className={`w-10 h-5 rounded-full p-0.5 transition-colors ${profile.athenaPresets[preset.id] ? 'bg-[#EC4899]' : 'bg-slate-700'}`} data-id="element-2490">
                                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${profile.athenaPresets[preset.id] ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-2491" />
                              </div>
                            </button>)}
                        </div>
                      </div>)}
                </div>
              </div>
            </Card>}

          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2492">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2493">
                <Bell size={20} className="text-[#EC4899]" data-id="element-2494" />
                Notification Preferences
              </h2>

              <div className="space-y-8" data-id="element-2495">
                <div className="space-y-4" data-id="element-2496">
                  <h3 className="text-sm font-medium text-slate-300" data-id="element-2497">
                    Delivery Channels
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2498">
                    <label className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-colors ${profile.notifChannels.inApp ? 'bg-slate-800/80 border-slate-700' : 'bg-[#1E293B]/50 border-slate-800 hover:bg-[#1E293B]'}`} data-id="element-2499">
                      <div className="flex justify-between items-start mb-2" data-id="element-2500">
                        <Laptop size={20} className={profile.notifChannels.inApp ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2501" />
                        <input type="checkbox" className="accent-pink-500" checked={profile.notifChannels.inApp} onChange={e => updateProfile({
                      notifChannels: {
                        ...profile.notifChannels,
                        inApp: e.target.checked
                      }
                    })} data-id="element-2502" />
                      </div>
                      <span className={`font-medium ${profile.notifChannels.inApp ? 'text-white' : 'text-slate-400'}`} data-id="element-2503">
                        In-App
                      </span>
                      <span className="text-xs text-slate-500 mt-1" data-id="element-2504">
                        Platform alerts
                      </span>
                    </label>
                    <label className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-colors ${profile.notifChannels.push ? 'bg-slate-800/80 border-slate-700' : 'bg-[#1E293B]/50 border-slate-800 hover:bg-[#1E293B]'}`} data-id="element-2505">
                      <div className="flex justify-between items-start mb-2" data-id="element-2506">
                        <Smartphone size={20} className={profile.notifChannels.push ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2507" />
                        <input type="checkbox" className="accent-pink-500" checked={profile.notifChannels.push} onChange={e => updateProfile({
                      notifChannels: {
                        ...profile.notifChannels,
                        push: e.target.checked
                      }
                    })} data-id="element-2508" />
                      </div>
                      <span className={`font-medium ${profile.notifChannels.push ? 'text-white' : 'text-slate-400'}`} data-id="element-2509">
                        Push
                      </span>
                      <span className="text-xs text-slate-500 mt-1" data-id="element-2510">
                        Mobile device
                      </span>
                    </label>
                    <label className={`flex flex-col p-4 rounded-xl border cursor-pointer transition-colors ${profile.notifChannels.email ? 'bg-slate-800/80 border-slate-700' : 'bg-[#1E293B]/50 border-slate-800 hover:bg-[#1E293B]'}`} data-id="element-2511">
                      <div className="flex justify-between items-start mb-2" data-id="element-2512">
                        <Mail size={20} className={profile.notifChannels.email ? 'text-[#EC4899]' : 'text-slate-500'} data-id="element-2513" />
                        <input type="checkbox" className="accent-pink-500" checked={profile.notifChannels.email} onChange={e => updateProfile({
                      notifChannels: {
                        ...profile.notifChannels,
                        email: e.target.checked
                      }
                    })} data-id="element-2514" />
                      </div>
                      <span className={`font-medium ${profile.notifChannels.email ? 'text-white' : 'text-slate-400'}`} data-id="element-2515">
                        Email
                      </span>
                      <span className="text-xs text-slate-500 mt-1" data-id="element-2516">
                        Inbox delivery
                      </span>
                    </label>
                  </div>
                  <div className="space-y-2 mt-4" data-id="element-2517">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2518">
                      Slack Webhook URL
                    </label>
                    <Input placeholder="https://hooks.slack.com/services/..." value={profile.notifChannels.slackWebhook} onChange={e => updateProfile({
                  notifChannels: {
                    ...profile.notifChannels,
                    slackWebhook: e.target.value
                  }
                })} data-id="element-2519" />
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-800 space-y-4" data-id="element-2520">
                  <div className="flex items-center justify-between" data-id="element-2521">
                    <div data-id="element-2522">
                      <h3 className="text-sm font-medium text-white" data-id="element-2523">
                        Quiet Hours
                      </h3>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-2524">
                        Mute non-critical notifications during these hours.
                      </p>
                    </div>
                    <div className={`w-10 h-5 rounded-full p-0.5 cursor-pointer transition-colors ${profile.quietHoursEnabled ? 'bg-[#EC4899]' : 'bg-slate-700'}`} onClick={() => updateProfile({
                  quietHoursEnabled: !profile.quietHoursEnabled
                })} data-id="element-2525">
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${profile.quietHoursEnabled ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-2526" />
                    </div>
                  </div>

                  {profile.quietHoursEnabled && <div className="flex items-center gap-4 bg-[#1E293B]/50 p-4 rounded-xl border border-slate-800" data-id="element-2527">
                      <Clock size={18} className="text-slate-400" data-id="element-2528" />
                      <div className="flex items-center gap-2" data-id="element-2529">
                        <input type="time" value={profile.quietHoursStart} onChange={e => updateProfile({
                    quietHoursStart: e.target.value
                  })} className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-sm text-white focus:outline-none focus:border-pink-500" data-id="element-2530" />
                        <span className="text-slate-500" data-id="element-2531">to</span>
                        <input type="time" value={profile.quietHoursEnd} onChange={e => updateProfile({
                    quietHoursEnd: e.target.value
                  })} className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1 text-sm text-white focus:outline-none focus:border-pink-500" data-id="element-2532" />
                      </div>
                    </div>}
                </div>

                <div className="pt-6 border-t border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-2533">
                  <div className="space-y-3" data-id="element-2534">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2535">
                      Severity Threshold
                    </label>
                    <div className="space-y-2" data-id="element-2536">
                      {[{
                    id: 'all',
                    label: 'All notifications'
                  }, {
                    id: 'warning_critical',
                    label: 'Warning & Critical only'
                  }, {
                    id: 'critical',
                    label: 'Critical only'
                  }].map(opt => <label key={opt.id} className="flex items-center gap-3 cursor-pointer" data-id="element-2537">
                          <input type="radio" name="severity" className="accent-pink-500" checked={profile.severityThreshold === opt.id} onChange={() => updateProfile({
                      severityThreshold: opt.id as any
                    })} data-id="element-2538" />
                          <span className="text-sm text-slate-300" data-id="element-2539">
                            {opt.label}
                          </span>
                        </label>)}
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-2540">
                    <label className="text-sm font-medium text-slate-300" data-id="element-2541">
                      Digest Frequency
                    </label>
                    <div className="space-y-2" data-id="element-2542">
                      {[{
                    id: 'realtime',
                    label: 'Real-time (As they happen)'
                  }, {
                    id: 'hourly',
                    label: 'Hourly summary'
                  }, {
                    id: 'daily',
                    label: 'Daily digest'
                  }].map(opt => <label key={opt.id} className="flex items-center gap-3 cursor-pointer" data-id="element-2543">
                          <input type="radio" name="digest" className="accent-pink-500" checked={profile.digestFrequency === opt.id} onChange={() => updateProfile({
                      digestFrequency: opt.id as any
                    })} data-id="element-2544" />
                          <span className="text-sm text-slate-300" data-id="element-2545">
                            {opt.label}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </div>
            </Card>}

          {/* API KEYS TAB */}
          {activeTab === 'api' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2546">
              <div className="flex items-center justify-between mb-6" data-id="element-2547">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2" data-id="element-2548">
                  <Key size={20} className="text-[#EC4899]" data-id="element-2549" />
                  API Keys
                </h2>
                <Button className="gap-2" onClick={handleGenerateKey} data-id="element-2550">
                  <Plus size={16} data-id="element-2551" />
                  Generate New Key
                </Button>
              </div>

              <div className="space-y-6" data-id="element-2552">
                {profile.apiKeys.length === 0 ? <div className="text-center py-12 bg-[#1E293B]/30 rounded-xl border border-slate-800 border-dashed" data-id="element-2553">
                    <Key size={32} className="mx-auto text-slate-600 mb-3" data-id="element-2554" />
                    <h3 className="text-slate-300 font-medium mb-1" data-id="element-2555">
                      No API keys found
                    </h3>
                    <p className="text-slate-500 text-sm" data-id="element-2556">
                      Generate a key to access Jua data programmatically.
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-2557">
                    <table className="w-full text-sm text-left" data-id="element-2558">
                      <thead data-id="element-2559">
                        <tr className="text-slate-500 border-b border-slate-800" data-id="element-2560">
                          <th className="pb-3 font-medium" data-id="element-2561">Name & Key</th>
                          <th className="pb-3 font-medium" data-id="element-2562">Permissions</th>
                          <th className="pb-3 font-medium" data-id="element-2563">
                            Created / Last Used
                          </th>
                          <th className="pb-3 font-medium" data-id="element-2564">Usage Today</th>
                          <th className="pb-3 font-medium text-right" data-id="element-2565">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800/50" data-id="element-2566">
                        {profile.apiKeys.map(key => <tr key={key.id} className="group" data-id="element-2567">
                            <td className="py-4" data-id="element-2568">
                              <div className="font-medium text-slate-200" data-id="element-2569">
                                {key.name}
                              </div>
                              <div className="font-mono text-xs text-slate-500 mt-1" data-id="element-2570">
                                {key.key}
                              </div>
                            </td>
                            <td className="py-4" data-id="element-2571">
                              <Badge variant="outline" data-id="element-2572">{key.permissions}</Badge>
                            </td>
                            <td className="py-4" data-id="element-2573">
                              <div className="text-slate-300" data-id="element-2574">
                                {key.created}
                              </div>
                              <div className="text-xs text-slate-500 mt-1" data-id="element-2575">
                                {key.lastUsed}
                              </div>
                            </td>
                            <td className="py-4" data-id="element-2576">
                              <div className="flex items-center gap-3" data-id="element-2577">
                                <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden w-24" data-id="element-2578">
                                  <div className="h-full bg-[#EC4899]" style={{
                            width: `${key.callsToday / key.rateLimit * 100}%`
                          }} data-id="element-2579" />
                                </div>
                                <span className="text-xs text-slate-400 font-mono" data-id="element-2580">
                                  {key.callsToday.toLocaleString()}/
                                  {key.rateLimit.toLocaleString()}
                                </span>
                              </div>
                            </td>
                            <td className="py-4 text-right" data-id="element-2581">
                              <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => handleDeleteKey(key.id)} data-id="element-2582">
                                <Trash2 size={16} data-id="element-2583" />
                              </Button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}

                <div className="pt-6 border-t border-slate-800" data-id="element-2584">
                  <h3 className="text-md font-semibold text-white mb-4 flex items-center gap-2" data-id="element-2585">
                    <Zap size={16} className="text-[#EC4899]" data-id="element-2586" />
                    Jua Query Engine API Key
                  </h3>
                  <p className="text-sm text-slate-400 mb-3" data-id="element-2587">
                    Connect to the Jua API for real multi-model weather
                    forecasts (EPT-2, EC-IFS, GFS, etc.). Get your key from the{' '}
                    <a href="https://developer.jua.ai" target="_blank" rel="noopener noreferrer" className="text-[#EC4899] hover:underline" data-id="element-2588">
                      developer portal
                    </a>
                    .
                  </p>
                  <div className="flex gap-3" data-id="element-2589">
                    <Input type="password" placeholder="API_KEY_ID:API_KEY_SECRET" className="flex-1 font-mono" defaultValue={(() => {
                  try {
                    return localStorage.getItem('jua_api_key') || '';
                  } catch {
                    return '';
                  }
                })()} id="jua-api-key-input" data-id="element-2590" />
                    <Button variant="secondary" onClick={() => {
                  const input = document.getElementById('jua-api-key-input') as HTMLInputElement;
                  if (input) {
                    localStorage.setItem('jua_api_key', input.value.trim());
                    toast.success(input.value.trim() ? 'Jua API key saved' : 'Jua API key removed');
                  }
                }} data-id="element-2591">
                      <Save size={16} className="mr-1.5" data-id="element-2592" />
                      Save Key
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2" data-id="element-2593">
                    Format:{' '}
                    <code className="text-slate-400" data-id="element-2594">KEY_ID:KEY_SECRET</code> ·
                    Stored locally in your browser
                  </p>
                </div>

                <div className="pt-6 border-t border-slate-800" data-id="element-2595">
                  <h3 className="text-md font-semibold text-white mb-4" data-id="element-2596">
                    Webhook Configuration
                  </h3>
                  <div className="flex gap-3" data-id="element-2597">
                    <Input placeholder="https://your-domain.com/webhook" className="flex-1" defaultValue="https://api.energy-corp.com/v1/jua-ingest" data-id="element-2598" />
                    <Button variant="secondary" data-id="element-2599">Test Webhook</Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2" data-id="element-2600">
                    We will send a POST request to this URL when alert
                    thresholds are met.
                  </p>
                </div>
              </div>
            </Card>}

          {/* SECURITY TAB */}
          {activeTab === 'security' && <Card className="p-6 border-slate-800 bg-[#111827]" data-id="element-2601">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2" data-id="element-2602">
                <Shield size={20} className="text-[#EC4899]" data-id="element-2603" />
                Security Settings
              </h2>

              <div className="space-y-8" data-id="element-2604">
                <div className="space-y-4" data-id="element-2605">
                  <h3 className="text-sm font-medium text-white" data-id="element-2606">
                    Change Password
                  </h3>
                  <div className="space-y-3 max-w-md" data-id="element-2607">
                    <Input type="password" placeholder="Current Password" data-id="element-2608" />
                    <Input type="password" placeholder="New Password" data-id="element-2609" />
                    <Input type="password" placeholder="Confirm New Password" data-id="element-2610" />
                    <Button variant="secondary" className="mt-2" data-id="element-2611">
                      Update Password
                    </Button>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-800 flex items-center justify-between" data-id="element-2612">
                  <div data-id="element-2613">
                    <h3 className="text-sm font-medium text-white" data-id="element-2614">
                      Two-Factor Authentication (2FA)
                    </h3>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-2615">
                      Add an extra layer of security to your account.
                    </p>
                  </div>
                  <Button variant="outline" className="border-slate-700 text-slate-300" data-id="element-2616">
                    Enable 2FA
                  </Button>
                </div>

                <div className="pt-6 border-t border-slate-800 space-y-4" data-id="element-2617">
                  <div className="flex items-center justify-between" data-id="element-2618">
                    <h3 className="text-sm font-medium text-white" data-id="element-2619">
                      Active Sessions
                    </h3>
                    <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300 hover:bg-red-400/10" data-id="element-2620">
                      Sign out all devices
                    </Button>
                  </div>

                  <div className="space-y-3" data-id="element-2621">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-800/50 border border-slate-700/50" data-id="element-2622">
                      <div className="flex items-center gap-3" data-id="element-2623">
                        <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center text-emerald-500" data-id="element-2624">
                          <Laptop size={18} data-id="element-2625" />
                        </div>
                        <div data-id="element-2626">
                          <div className="text-sm font-medium text-white flex items-center gap-2" data-id="element-2627">
                            Mac OS · Chrome
                            <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-emerald-500/30 text-emerald-400 bg-emerald-500/10" data-id="element-2628">
                              CURRENT
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2629">
                            Frankfurt, Germany · Active now
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-xl bg-[#1E293B]/30 border border-slate-800" data-id="element-2630">
                      <div className="flex items-center gap-3" data-id="element-2631">
                        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-400" data-id="element-2632">
                          <Smartphone size={18} data-id="element-2633" />
                        </div>
                        <div data-id="element-2634">
                          <div className="text-sm font-medium text-slate-300" data-id="element-2635">
                            iOS · Safari
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-2636">
                            Frankfurt, Germany · Last active 2 hours ago
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-slate-500" data-id="element-2637">
                        Revoke
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>}
        </div>
      </div>

      {/* Sticky Save Bar */}
      {isDirty && <div className="fixed bottom-0 left-0 right-0 p-4 bg-slate-900/90 backdrop-blur-xl border-t border-slate-800 z-50 flex justify-center animate-in slide-in-from-bottom-4" data-id="element-2638">
          <div className="flex items-center justify-between w-full max-w-5xl px-4" data-id="element-2639">
            <div className="flex items-center gap-3" data-id="element-2640">
              <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" data-id="element-2641" />
              <span className="text-sm font-medium text-amber-500" data-id="element-2642">
                You have unsaved changes
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-2643">
              <Button variant="ghost" onClick={resetProfile} className="text-slate-400 hover:text-white" data-id="element-2644">
                Discard
              </Button>
              <Button className="gap-2 bg-[#EC4899] hover:bg-pink-600 text-white" onClick={handleSave} data-id="element-2645">
                <Save size={16} data-id="element-2646" />
                Save Changes
              </Button>
            </div>
          </div>
        </div>}
    </div>;
}